// ─── Public Status Page — last line of defence ───────────────────────
//
// A render error here must NOT reach the app's client error pipeline
// (lib/error-reporter.ts): that posts to /api/app-errors, which is behind auth,
// so an anonymous visitor would get a 401 for every frame — and the API it is
// trying to report to is very often the thing that is already down. Worse, a
// storm of failing report requests during an outage is load we are adding to a
// dying process. console.error only, and nothing else.

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { da } from '../i18n/da';
import { SIcon } from '../ui/SIcon';

interface StatusErrorBoundaryProps {
  children: ReactNode;
}

interface StatusErrorBoundaryState {
  hasError: boolean;
}

export class StatusErrorBoundary extends Component<StatusErrorBoundaryProps, StatusErrorBoundaryState> {
  state: StatusErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): StatusErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Local only — see the file header.
    console.error('[status] render failed', error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="sp-fatal" role="alert">
        <div className="sp-fatal-card">
          <SIcon name="degraded" size={28} className="sp-fatal-icon" />
          <h1 className="sp-fatal-title">{da.fatalTitle}</h1>
          <p className="sp-fatal-body">{da.fatalBody}</p>
          <button type="button" className="sp-btn sp-btn--primary" onClick={this.handleReload}>
            {da.reload}
          </button>
        </div>
      </div>
    );
  }
}
